import { Cpu, Router, RotateCw, HelpCircle, type LucideIcon } from "lucide-react";
import type { TelemetryOutageCauseKey } from "../../data/types";

export function formatOutageDuration(minutes: number | null): string {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

export function formatOutageCount(count: number): string {
  return `${count} outage${count === 1 ? "" : "s"}`;
}

// ─── Date/time formatting ──────────────────────────────────────────────────────
// The backend pre-converts every timestamp to the facility's local time and
// embeds the correct UTC offset (e.g. "2026-07-22T12:57:00-04:00") — these
// helpers read the wall-clock digits straight out of the string instead of
// going through `new Date(...).toLocaleString()`, which would silently
// re-convert to the *viewer's* system timezone and show the wrong time.
// Date-only strings ("2026-07-22", no offset) have the same problem in the
// other direction: `new Date("2026-07-22")` parses as UTC midnight, so
// `toLocaleDateString()` can roll it back to the previous day for any viewer
// west of UTC. Do not swap these for `new Date(...).toLocaleX()` calls.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatOutageDate(isoDate: string): string {
  const [, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return `${MONTHS[month - 1]} ${day}`;
}

export function formatOutageTime(isoDateTime: string): string {
  const match = isoDateTime.match(/T(\d{2}):(\d{2})/);
  if (!match) return "";
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
}

// ─── Cause display ────────────────────────────────────────────────────────────
// The backend only returns the raw stored cause key (e.g. "site",
// "joulesedge") with no display label — this is the single place that maps
// those keys to what's shown on screen, reused by both the outage alert card
// and the outage history table so the mapping never drifts between them.
interface CauseDisplay {
  label: string;
  icon: LucideIcon;
}

const CAUSE_DISPLAY: Record<string, CauseDisplay> = {
  site: { label: "Site / dispatch service down", icon: Cpu },
  emulator: { label: "Site / dispatch service down", icon: Cpu },
  joulesedge: { label: "JoulesEdge network outage", icon: Router },
  component_restart: { label: "Component restart", icon: RotateCw },
};

export function getCauseDisplay(causeKey: TelemetryOutageCauseKey): CauseDisplay {
  // Guards against a non-string value reaching this boundary — e.g. a stale
  // IndexedDB-persisted query cache from before a response shape change,
  // rehydrated before a fresh fetch has had a chance to correct it. Falls
  // back to the same "Undiagnosed" treatment as a genuinely missing cause
  // rather than crashing the page on unexpected input.
  if (typeof causeKey !== "string" || causeKey.length === 0) {
    return { label: "Undiagnosed", icon: HelpCircle };
  }
  return CAUSE_DISPLAY[causeKey] ?? { label: humanize(causeKey), icon: HelpCircle };
}

function humanize(key: string): string {
  return key
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// ─── Cause filter options ──────────────────────────────────────────────────────
// Label and value both match the backend's own cause filter vocabulary
// (site/joulesedge/component_restart/unknown) exactly — no relabeling. This is separate from
// getCauseDisplay above, which does map a raw "emulator" record to the
// "Site / dispatch service down" display text for individual outage rows;
// that per-row mapping is unrelated to this filter and stays as-is.
export interface CauseFilterOption {
  value: string;
  label: string;
}

export const CAUSE_FILTER_OPTIONS: CauseFilterOption[] = [
  { value: "all", label: "All" },
  { value: "site", label: "Site" },
  { value: "joulesedge", label: "JoulesEdge" },
  { value: "component_restart", label: "Component restart" },
  { value: "unknown", label: "Unknown" },
];

// ─── Metric card value coloring ───────────────────────────────────────────────
// Outages and Troubled Recoveries are colored by their OWN value: neutral
// dark at zero, amber once there's something to draw attention to. Confirmed
// against both Figma states (active outage and healthy) — amber whenever
// non-zero in either state.
export function metricValueClass(value: number): string {
  return value > 0 ? "text-[var(--custom-amber-200)]" : "text-gray-900";
}

// Downtime and 7-Day Uptime instead mirror the card's overall state, NOT
// their own value — confirmed via Figma: a healthy facility with a non-zero
// "6 min" of downtime this week still renders it in plain dark text, and a
// healthy facility's 99.94% uptime still renders green (not gated on being
// exactly 100%). Red/amber only while an outage is actively ongoing.
export function downtimeValueClass(isOngoing: boolean): string {
  return isOngoing ? "text-[var(--custom-red-600)]" : "text-gray-900";
}

export function uptimeValueClass(isOngoing: boolean): string {
  return isOngoing ? "text-[var(--custom-amber-200)]" : "text-[var(--utility-success-500)]";
}
